import { useState, useRef, useEffect } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '../../hooks/use-notifications';
import { useAuthStore } from '../../stores/auth-store';
import { useNavigate } from 'react-router-dom';
import { timeAgo } from '../../lib/utils';

export function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const { data: unreadData } = useNotifications(1, true);
  const unreadCount = unreadData?.meta.pagination?.total ?? 0;

  const { data: allData } = useNotifications(1, false);
  const notifications = allData?.data ?? [];

  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNotificationClick = (notification: typeof notifications[0]) => {
    if (!notification.readAt) {
      markRead.mutate(notification.id);
    }
    const payload = notification.payload as { ticketId?: string };
    if (payload.ticketId) {
      const isClient = user?.role === 'CLIENT';
      navigate(isClient ? `/tickets/${payload.ticketId}` : `/admin/tickets/${payload.ticketId}`);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[10px] text-white">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile backdrop */}
          <div className="fixed inset-0 z-40 bg-black/20 md:hidden" onClick={() => setOpen(false)} />
          <div className="fixed inset-x-3 top-16 z-50 md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-dark-border dark:bg-dark-card">
          <div className="flex items-center justify-between border-b px-4 py-3 dark:border-dark-border">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text">Notificări</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <CheckCheck size={14} />
                Marchează toate citite
              </button>
            )}
          </div>
          <div className="max-h-[70vh] md:max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-400 dark:text-dark-muted">Nicio notificare</p>
            ) : (
              notifications.slice(0, 20).map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-dark-border/50 ${!n.readAt ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                >
                  <div className="mt-1 shrink-0">
                    {n.readAt ? (
                      <Check size={14} className="text-gray-300" />
                    ) : (
                      <div className="h-2 w-2 rounded-full bg-blue-500" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-700 line-clamp-2 dark:text-dark-text">{(n.payload as { message?: string }).message ?? 'Notificare nouă'}</p>
                    <p className="mt-0.5 text-xs text-gray-400 dark:text-dark-muted">{timeAgo(n.createdAt)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
